import { useState } from "react";
import { Mail, MicOff, PhoneIncoming, PhoneOutgoing, Sparkles } from "lucide-react";
import { fmtDuration, fmtPhone, fmtShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { EmptyState, Pill, ScoreBadge, Section, sentimentTone } from "@/components/common";
import { cn } from "@/lib/utils";

function TimelineItem({ c, threshold, onOpen }) {
  const a = c.analysis || {};
  const below = c.score != null && c.score < threshold;
  return (
    <li className="relative pl-8" data-testid={`timeline-item-${c.id}`}>
      <span className={cn("absolute left-[9px] top-2 h-3 w-3 rounded-full border-2 border-[#0f172a]", c.score == null ? "bg-slate-500" : below ? "bg-red-400" : c.score >= 90 ? "bg-emerald-400" : "bg-blue-400")} />
      <button onClick={() => onOpen(c.id)} className="w-full text-left panel panel-hover p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-mono text-slate-500">{fmtShort(c.created_at)}</span>
          {c.direction === "incoming" ? <PhoneIncoming className="h-3 w-3 text-cyan-400" /> : <PhoneOutgoing className="h-3 w-3 text-blue-400" />}
          <span className="text-[11px] font-mono text-slate-400">{fmtPhone(c.client_number)} · {fmtDuration(c.duration)}</span>
          <span className="ml-auto flex items-center gap-1.5">
            {c.recording_status === "missing" && <Pill tone="red"><MicOff className="h-3 w-3" /> not recorded</Pill>}
            {c.feedback_email?.status === "sent" && <Pill tone={c.feedback_email.cc_applied ? "amber" : "blue"}><Mail className="h-3 w-3" /> {c.feedback_email.cc_applied ? "emailed + CC" : "emailed"}</Pill>}
            <ScoreBadge score={c.score} size="sm" />
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-semibold text-slate-100">{c.topic || a.summary?.slice(0, 60) || "Customer call"}</span>
          {c.call_type && <Pill tone="violet">{c.call_type}</Pill>}
          {c.sentiment && <Pill tone={sentimentTone(c.sentiment)}>{c.sentiment}</Pill>}
          {a.evaluable === false && <Pill tone="amber">not evaluable</Pill>}
        </div>
        {a.evaluable === false ? (
          <p className="text-xs text-slate-500">{a.not_evaluable_reason}</p>
        ) : (
          <>
            {a.coaching_message && <p className="text-xs text-slate-300 leading-relaxed line-clamp-3">{a.coaching_message}</p>}
            {a.improvements?.length > 0 && (
              <ul className="space-y-0.5">
                {a.improvements.slice(0, 2).map((im, i) => <li key={i} className="text-[11px] text-amber-200/90 pl-2 border-l border-amber-500/40 leading-snug">{im}</li>)}
              </ul>
            )}
          </>
        )}
      </button>
    </li>
  );
}

export function CoachingTimeline({ items, threshold, onOpen, className }) {
  const [belowOnly, setBelowOnly] = useState(false);
  const [limit, setLimit] = useState(20);
  const filtered = (items || []).filter((c) => !belowOnly || (c.score != null && c.score < threshold));
  return (
    <Section
      title="Coaching timeline"
      eyebrow={`${items?.length || 0} reviewed calls · newest first`}
      className={className}
      testId="scorecard-timeline"
      actions={
        <Button data-testid="timeline-below-toggle" size="sm" variant="outline" className={cn("h-7 text-xs border-[#2d3d58] bg-[#0f172a]", belowOnly && "border-red-500/40 text-red-300")} onClick={() => setBelowOnly((v) => !v)}>
          {belowOnly ? `Below ${threshold} only` : "All calls"}
        </Button>
      }
    >
      {filtered.length ? (
        <>
          <ol className="relative space-y-3 before:absolute before:left-[14px] before:top-2 before:bottom-2 before:w-px before:bg-[#1f293d]">
            {filtered.slice(0, limit).map((c) => <TimelineItem key={c.id} c={c} threshold={threshold} onOpen={onOpen} />)}
          </ol>
          {filtered.length > limit && (
            <div className="mt-3 text-center"><Button data-testid="timeline-show-more" size="sm" variant="ghost" className="text-xs text-slate-400" onClick={() => setLimit((l) => l + 20)}>Show {Math.min(20, filtered.length - limit)} more</Button></div>
          )}
        </>
      ) : (
        <EmptyState icon={Sparkles} title={belowOnly ? "No calls below threshold" : "No reviewed calls in this range"} hint={belowOnly ? "Nice — every reviewed call met the bar." : "Coaching entries appear as GPT-5.4 reviews this agent's calls."} />
      )}
    </Section>
  );
}
